import React, { useState, useEffect } from "react"; // Added useEffect
import { X, Eye, EyeOff } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { auth, createUserWithEmailAndPassword, db, googleProvider } from "./firebase";
import { doc, setDoc } from "firebase/firestore";
import { signInWithPopup } from "firebase/auth";
import { createUserData } from "./utils/userData";

const SignupModal = ({ isOpen, onClose, onSwitchToLogin }) => {
  const initialFormState = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    acceptTerms: false,
  };

  const [form, setForm] = useState(initialFormState);

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setForm(initialFormState);
      setErrors({});
      setApiError(null);
      setLoading(false);
    }
  }, [isOpen]); // Run when isOpen changes

  if (!isOpen) return null;

  const handleGoogleLogin = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      await createUserData(user);
      onClose();
      console.log("Usuario registrado/logueado con Google:", user);
    } catch (error) {
      if (error.code === "auth/popup-closed-by-user") {
        console.log("El usuario cerró el popup de Google.");
      } else {
        console.error("Error al iniciar sesión con Google:", error);
        setApiError("Failed to log in with Google. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = "First name is required.";
    if (!form.lastName.trim()) newErrors.lastName = "Last name is required.";
    if (!form.email.includes("@")) newErrors.email = "Email is not valid.";
    if (form.password.length < 8) newErrors.password = "Password must be at least 8 characters.";
    if (!form.acceptTerms) newErrors.acceptTerms = "You must accept the terms.";
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    const newErrors = validate();
    if (Object.keys(newErrors).length === 0) {
      setErrors({});
      setLoading(true);
      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          form.email,
          form.password
        );
        const user = userCredential.user;

        await setDoc(doc(db, "users", user.uid), {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          // You might want to add a timestamp here, e.g., createdAt: serverTimestamp()
        });

        console.log("User signed up and data saved to Firestore:", user.uid);
        onClose(); // Close modal on successful signup
      } catch (error) {
        console.error("Error signing up:", error.code, error.message);
        let errorMessage = "Failed to sign up. Please try again.";
        if (error.code === "auth/email-already-in-use") {
          errorMessage = "This email is already in use.";
        } else if (error.code === "auth/invalid-email") {
          errorMessage = "The email address is not valid.";
        } else if (error.code === "auth/weak-password") {
          errorMessage = "The password is too weak. It must be at least 6 characters.";
        }
        setApiError(errorMessage);
      } finally {
        setLoading(false);
      }
    } else {
      setErrors(newErrors);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-center items-center px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-black">
          <X />
        </button>

        <h2 className="text-3xl font-bold text-gray-900 mb-2">Sign Up</h2>
        <p className="text-sm text-gray-500 mb-6">
          Already have an account?{" "}
          <span onClick={onSwitchToLogin} className="text-orange-500 font-semibold cursor-pointer">
            Login
          </span>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-3">
            <div className="w-1/2">
              <input
                type="text"
                name="firstName"
                placeholder="first name"
                value={form.firstName}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-100 rounded-xl text-black outline-none"
                disabled={loading}
              />
              {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
            </div>
            <div className="w-1/2">
              <input
                type="text"
                name="lastName"
                placeholder="last name"
                value={form.lastName}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-100 rounded-xl text-black outline-none"
                disabled={loading}
              />
              {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
            </div>
          </div>

          <div>
            <input
              type="email"
              name="email"
              placeholder="email@address.com"
              value={form.email}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-100 rounded-xl text-black outline-none"
              disabled={loading}
              autoComplete="email"
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="at least 8 characters long"
              value={form.password}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-100 rounded-xl text-black outline-none pr-10"
              disabled={loading}
              autoComplete="new-password"
            />
            <span
              onClick={() => setShowPassword(!showPassword)}
              className="absolute top-3 right-3 cursor-pointer text-gray-400"
            >
              {showPassword ? <EyeOff size={1} /> : <Eye size={1} />}
            </span>
            {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              name="acceptTerms"
              checked={form.acceptTerms}
              onChange={handleChange}
              disabled={loading}
            />
            I accept terms and conditions & privacy policy
          </label>
          {errors.acceptTerms && <p className="text-xs text-red-500">{errors.acceptTerms}</p>}

          {apiError && <p className="text-xs text-red-500 text-center mt-2">{apiError}</p>}

          <button
            type="submit"
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-xl shadow-lg transition-all"
            disabled={loading}
          >
            {loading ? "Signing Up..." : "Submit"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-500">
          Or sign up with social media
        </div>

        <div className="flex justify-center mt-4">
          <button
            onClick={handleGoogleLogin}
            type="button"
            className="rounded-full bg-white shadow-lg p-3 hover:shadow-xl transition flex items-center justify-center"
            disabled={loading}
          >
            <FcGoogle size={32} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignupModal;
